/** ShipStation order export lifecycle stored on orders.metadata.shipstation */
export type ShipStationExportStatus = 'pending' | 'exported' | 'failed' | 'blocked';

export type ShipStationExportStatusFilter = ShipStationExportStatus | 'not_submitted';

export interface ShipStationOrderMetadata {
  orderId?: number;
  orderKey?: string;
  storeId?: string;
  exportStatus?: ShipStationExportStatus;
  exportedAt?: string;
  lastError?: string;
  batchId?: string;
  shipmentId?: string;
  trackingNumber?: string;
  labelUrl?: string;
}

export const ELIGIBLE_ORDER_STATUSES: ReadonlySet<string> = new Set(['paid', 'processing']);

export interface ShipStationExportResultItem {
  orderId: string;
  exportStatus: ShipStationExportStatus | 'skipped';
  ssOrderId?: number;
  error?: string;
}

export interface ShipStationExportSummary {
  exported: number;
  failed: number;
  blocked: number;
  skipped: number;
  results: ShipStationExportResultItem[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Stable idempotent key for ShipStation V1 createorder. */
export function buildShipStationOrderKey(storeId: string, orderId: string): string {
  return `landi:${storeId}:${orderId}`;
}

export function parseShipStationOrderMetadata(
  metadata: Record<string, unknown> | undefined | null
): ShipStationOrderMetadata | undefined {
  if (!metadata) {
    return undefined;
  }
  const shipstation = metadata.shipstation;
  if (!isRecord(shipstation)) {
    return undefined;
  }
  return shipstation as ShipStationOrderMetadata;
}

export function getShipStationExportStatus(
  metadata: Record<string, unknown> | undefined | null
): ShipStationExportStatus | undefined {
  return parseShipStationOrderMetadata(metadata)?.exportStatus;
}

export function isExportStatusEligibleForSubmit(
  metadata: Record<string, unknown> | undefined | null
): boolean {
  const status = getShipStationExportStatus(metadata);
  return status === undefined || status === 'pending';
}

export function matchesIntegrationExportStatusFilter(
  metadata: Record<string, unknown> | undefined | null,
  filter: ShipStationExportStatusFilter,
  provider: string = 'shipstation'
): boolean {
  // In the future, we can check provider-specific metadata.
  // For now, we fall back to shipstation metadata.
  const status = getShipStationExportStatus(metadata);
  switch (filter) {
    case 'not_submitted':
      return status === undefined || status === 'pending';
    case 'pending':
      return status === 'pending';
    case 'exported':
      return status === 'exported';
    case 'failed':
      return status === 'failed';
    case 'blocked':
      return status === 'blocked';
    default:
      return false;
  }
}

export const matchesShipStationExportStatusFilter = matchesIntegrationExportStatusFilter;

export function parseIntegrationExportStatusFilter(
  value: string | null | undefined
): ShipStationExportStatusFilter | undefined {
  if (!value) {
    return undefined;
  }
  const normalized = value.trim().toLowerCase();
  if (
    normalized === 'not_submitted' ||
    normalized === 'pending' ||
    normalized === 'exported' ||
    normalized === 'failed' ||
    normalized === 'blocked'
  ) {
    return normalized;
  }
  return undefined;
}

export const parseShipStationExportStatusFilter = parseIntegrationExportStatusFilter;

/** Marks order metadata for async export after payment. */
export function withPendingExport(
  metadata: Record<string, unknown> | undefined | null
): Record<string, unknown> {
  const base: Record<string, unknown> = { ...(metadata ?? {}) };
  const existing = parseShipStationOrderMetadata(base);
  const currentStatus = existing?.exportStatus;
  if (
    currentStatus === 'pending' ||
    currentStatus === 'exported' ||
    currentStatus === 'failed' ||
    currentStatus === 'blocked'
  ) {
    return base;
  }
  base.shipstation = {
    ...existing,
    exportStatus: 'pending' satisfies ShipStationExportStatus,
  };
  return base;
}

export const withShipStationPendingExport = withPendingExport;

export function mergeShipStationOrderMetadata(
  metadata: Record<string, unknown> | undefined | null,
  patch: ShipStationOrderMetadata
): Record<string, unknown> {
  const base: Record<string, unknown> = { ...(metadata ?? {}) };
  const existing = parseShipStationOrderMetadata(base);
  base.shipstation = {
    ...existing,
    ...patch,
  };
  return base;
}
